import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Bookmark, FlaskConical, Search, ThumbsDown, ThumbsUp, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useDebouncedValue } from '@/lib/hooks/useDebouncedValue'
import { KnowledgeFrame } from '../components/KnowledgeNav'
import { ArtworkTile, WeightLabel } from '../components/ArtworkTile'
import { categoryColor } from '../components/categoryStyle'
import {
  useDeleteQuery, useEvaluate, useJudge, useSaveQuery, useSavedQueries, useSearchPreview,
} from '../hooks/useKnowledge'
import type { PreviewConcept, SavedQuery, SearchPreview } from '../api/knowledgeApi'

function ConceptMatches({ preview }: { preview: SearchPreview }) {
  const shown = preview.concepts.filter((c) => c.used || c.excluded || c.similarity >= preview.floor - 0.05).slice(0, 18)
  return (
    <div className="space-y-1.5">
      <div className="text-xs text-slate-500">
        Concepts matched (similarity to the query; only those above {preview.floor.toFixed(2)} count, scaled by their weight):
      </div>
      <div className="flex flex-wrap gap-1.5">
        {shown.map((c) => <MatchChip key={c.ucid} c={c} />)}
        {shown.length === 0 && <span className="text-sm text-slate-500">No concept is close enough to this query.</span>}
      </div>
    </div>
  )
}

function MatchChip({ c }: { c: PreviewConcept }) {
  const why = c.excluded ? (c.rejected ? 'removal pending' : 'hidden') : c.used ? null : 'below the cut-off'
  return (
    <Link
      to={`/app/knowledge/${c.ucid}`}
      title={why ?? `contributes ${c.contribution.toFixed(3)} per link`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
        c.used ? 'bg-white' : 'bg-slate-50 text-slate-400'
      } ${c.excluded ? 'line-through' : ''}`}
    >
      <span className="h-2 w-2 rounded-full" style={{ background: categoryColor(c.category) }} />
      {c.name}
      <span className="tabular-nums text-slate-400">{c.similarity.toFixed(2)}</span>
      {c.used && (
        <span className="text-[10px]">
          × <WeightLabel weight={c.effectiveWeight} curated={isCuratorWeight(c)} />
        </span>
      )}
      {why && <span className="text-[10px] no-underline">({why})</span>}
    </Link>
  )
}

// The curator's weight is in effect only in curated mode, where effectiveWeight came from it.
const isCuratorWeight = (c: PreviewConcept) => c.weight != null && c.effectiveWeight === c.weight

function ResultGrid({
  preview,
  verdicts,
  onJudge,
}: {
  preview: SearchPreview
  verdicts: Map<number, 1 | -1>
  onJudge?: (artworkId: number, verdict: 1 | -1 | null) => void
}) {
  const names = useMemo(() => new Map(preview.concepts.map((c) => [c.ucid, c.name])), [preview])
  if (!preview.artworks.length) return <p className="py-8 text-center text-sm text-slate-500">No artworks.</p>
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
      {preview.artworks.map((a, i) => {
        const v = verdicts.get(Number(a.id))
        return (
          <ArtworkTile
            key={a.id}
            artwork={a}
            footer={
              <div className="mt-0.5 space-y-0.5 text-[10px] text-slate-500">
                <div className="truncate" title={a.reasons.map((u) => names.get(u) ?? u).join(', ')}>
                  #{i + 1} · {a.reasons.slice(0, 2).map((u) => names.get(u) ?? '…').join(', ')}
                </div>
              </div>
            }
          >
            {v && (
              <div className={`absolute left-1 top-1 rounded px-1 text-[10px] font-medium ${v === 1 ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white'}`}>
                {v === 1 ? 'right' : 'wrong'}
              </div>
            )}
            {onJudge && (
              <div className="absolute right-1 top-1 flex gap-1 opacity-0 transition group-hover:opacity-100">
                <button
                  className={`rounded bg-white/90 p-1 shadow ${v === 1 ? 'text-emerald-600' : 'text-slate-500'}`}
                  title="Belongs in these results"
                  onClick={() => onJudge(Number(a.id), v === 1 ? null : 1)}
                >
                  <ThumbsUp className="h-3.5 w-3.5" />
                </button>
                <button
                  className={`rounded bg-white/90 p-1 shadow ${v === -1 ? 'text-rose-600' : 'text-slate-500'}`}
                  title="Does not belong"
                  onClick={() => onJudge(Number(a.id), v === -1 ? null : -1)}
                >
                  <ThumbsDown className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </ArtworkTile>
        )
      })}
    </div>
  )
}

function Evaluation() {
  const evaluate = useEvaluate()
  const rows = evaluate.data?.queries ?? []
  const pct = (x: number | null) => (x == null ? '—' : `${Math.round(x * 100)}%`)
  const totals = rows.reduce(
    (t, r) => ({
      cGood: t.cGood + r.curated.goodFound,
      rGood: t.rGood + r.raw.goodFound,
      cBad: t.cBad + r.curated.badInTop,
      rBad: t.rBad + r.raw.badInTop,
    }),
    { cGood: 0, rGood: 0, cBad: 0, rBad: 0 }
  )
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2 pb-3">
        <div>
          <CardTitle className="text-sm">Test set</CardTitle>
          <CardDescription>
            Every saved query with judgments, run curated and raw: how many known-right artworks reach
            the top {evaluate.data?.topK ?? 24}, and how many known-wrong ones get in.
          </CardDescription>
        </div>
        <Button size="sm" variant="outline" disabled={evaluate.isPending} onClick={() => evaluate.mutate()}>
          <FlaskConical className="mr-1.5 h-3.5 w-3.5" /> {evaluate.isPending ? 'Running…' : 'Run'}
        </Button>
      </CardHeader>
      {evaluate.data && (
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-sm text-slate-500">No judged queries yet. Save a query and mark some results right or wrong.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Query</TableHead>
                  <TableHead className="text-right">Right found (curated / raw)</TableHead>
                  <TableHead className="text-right">Wrong let in (curated / raw)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.query}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.curated.goodFound}/{r.good} ({pct(r.curated.goodRecall)}) · {r.raw.goodFound} ({pct(r.raw.goodRecall)})
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.curated.badInTop} · {r.raw.badInTop}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="font-medium">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right tabular-nums">{totals.cGood} · {totals.rGood}</TableCell>
                  <TableCell className="text-right tabular-nums">{totals.cBad} · {totals.rBad}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      )}
    </Card>
  )
}

export function SearchPreviewPage() {
  const [q, setQ] = useState('')
  const [saved, setSaved] = useState<SavedQuery | null>(null)
  const [publishedOnly, setPublishedOnly] = useState(true)
  const [compare, setCompare] = useState(false)
  const query = useDebouncedValue(q, 400)
  const queryId = saved && saved.query.toLowerCase() === query.trim().toLowerCase() ? saved.id : undefined

  const curated = useSearchPreview({ q: query, mode: 'curated', publishedOnly, queryId })
  const raw = useSearchPreview({ q: compare ? query : '', mode: 'raw', publishedOnly, queryId })
  const { data: queries } = useSavedQueries()
  const saveQuery = useSaveQuery()
  const deleteQuery = useDeleteQuery()
  const judge = useJudge()

  const verdicts = useMemo(
    () => new Map((curated.data?.judgments ?? []).map((j) => [Number(j.artworkId), j.verdict])),
    [curated.data]
  )
  const onJudge = queryId ? (artworkId: number, verdict: 1 | -1 | null) => judge.mutate({ queryId, artworkId, verdict }) : undefined
  const error = (curated.error as Error | null)?.message

  return (
    <KnowledgeFrame
      title="Search preview"
      description={
        <>
          What the knowledge graph returns for a shopper&apos;s words, and why. The query is matched to
          concepts by meaning; artworks score by how strongly they carry those concepts, scaled by each
          concept&apos;s weight. <strong>Curated</strong> applies your weights, hides and removals;{' '}
          <strong>raw</strong> is the graph before curation. Save a query and mark results right or
          wrong to build a test set — the same set will check the Picasso search change.
        </>
      }
    >
      <div className="grid gap-6 lg:grid-cols-[16rem_1fr]">
        <div className="space-y-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Saved queries</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              {(queries?.data ?? []).length === 0 && <p className="text-xs text-slate-500">None yet.</p>}
              {queries?.data.map((s) => (
                <div
                  key={s.id}
                  className={`group flex items-center justify-between gap-2 rounded px-2 py-1 text-sm ${saved?.id === s.id ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
                >
                  <button className="min-w-0 flex-1 truncate text-left" onClick={() => { setSaved(s); setQ(s.query) }}>
                    {s.query}
                  </button>
                  <span className="shrink-0 text-[11px] tabular-nums">
                    <span className="text-emerald-700">{s.good}</span>/<span className="text-rose-600">{s.bad}</span>
                  </span>
                  <button
                    className="text-slate-300 opacity-0 hover:text-rose-600 group-hover:opacity-100"
                    title="Delete query and its judgments"
                    onClick={() => { deleteQuery.mutate(s.id); if (saved?.id === s.id) setSaved(null) }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-[18rem] flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                className="pl-8"
                placeholder="Try what a shopper would type: “moody blue seascape”, “gift for a nurse”…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              disabled={!query.trim() || saveQuery.isPending || !!queryId}
              onClick={() => saveQuery.mutate({ query: query.trim() }, { onSuccess: (s) => setSaved(s) })}
            >
              <Bookmark className="mr-1.5 h-3.5 w-3.5" /> {queryId ? 'Saved' : 'Save query'}
            </Button>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={publishedOnly} onCheckedChange={setPublishedOnly} /> Published only
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={compare} onCheckedChange={setCompare} /> Compare with raw
            </label>
          </div>
          {queryId && <p className="text-xs text-slate-500">Hover an artwork to mark it right or wrong for “{saved?.query}”.</p>}
          {error && <p className="text-sm text-rose-600">{error}</p>}

          {!query.trim() && <p className="py-12 text-center text-sm text-slate-500">Type a query to see what the graph returns.</p>}
          {query.trim() && curated.isLoading && <Skeleton className="h-96 w-full" />}

          {curated.data && query.trim() && (
            <div className={`grid gap-6 ${compare ? 'xl:grid-cols-2' : ''}`}>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Curated</CardTitle>
                  <ConceptMatches preview={curated.data} />
                </CardHeader>
                <CardContent className={curated.isFetching ? 'opacity-70' : undefined}>
                  <ResultGrid preview={curated.data} verdicts={verdicts} onJudge={onJudge} />
                </CardContent>
              </Card>
              {compare && raw.data && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Raw (before curation)</CardTitle>
                    <ConceptMatches preview={raw.data} />
                  </CardHeader>
                  <CardContent className={raw.isFetching ? 'opacity-70' : undefined}>
                    <ResultGrid preview={raw.data} verdicts={verdicts} onJudge={onJudge} />
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          <Evaluation />
        </div>
      </div>
    </KnowledgeFrame>
  )
}
